/**
 * Audit-trail timeline drawer for a campaign's changeLog subcollection.
 *
 * Renders a campaign's `changeLog` subcollection in reverse-chronological
 * order. Each entry: who, when, what field, before → after, source. Filter
 * pills at the top let the team show only human edits or only pipeline
 * activity. Lazy-loads on first open and refetches whenever the drawer
 * is reopened so a freshly-edited campaign reflects immediately.
 */

import React, { useEffect, useMemo, useState } from 'react';
import type {
  CgBoostChangeLogEntry,
  CgBoostChangeSource,
} from '../../types/cgBoost';
import { getCampaignChangeLog } from '../../services/cgBoostApi';
import { formatTimeAgo } from '../../utils/dateFormatters';
import { Modal, Pill, Button } from '../ui';

interface Props {
  open: boolean;
  onClose: () => void;
  canonicalCampaignId: string;
  campaignTitle?: string;
}

const SOURCE_LABEL: Record<CgBoostChangeSource, string> = {
  ui: 'Human',
  pipeline: 'Pipeline',
  admin: 'Admin tool',
};

const SOURCE_TONE: Record<CgBoostChangeSource, React.ComponentProps<typeof Pill>['tone']> = {
  ui: 'accent',
  pipeline: 'info',
  admin: 'warning',
};

const HistoryDrawer: React.FC<Props> = ({ open, onClose, canonicalCampaignId, campaignTitle }) => {
  const [entries, setEntries] = useState<CgBoostChangeLogEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CgBoostChangeSource | 'all'>('all');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEntries(null);
    (async () => {
      try {
        const data = await getCampaignChangeLog(canonicalCampaignId);
        if (!cancelled) setEntries(data);
      } catch (err: any) {
        if (!cancelled) setError(err.message ?? 'Failed to load changelog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, canonicalCampaignId]);

  const visible = useMemo(() => {
    if (!entries) return [];
    return filter === 'all' ? entries : entries.filter((e) => e.source === filter);
  }, [entries, filter]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="CG Boost"
      title="History"
      width={640}
      footer={
        <Button variant="ghost" size="md" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div style={body}>
        {campaignTitle && <div style={subtitle}>{campaignTitle}</div>}

        <div style={filterRow}>
          <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>All</FilterPill>
          <FilterPill active={filter === 'ui'} onClick={() => setFilter('ui')}>Human edits</FilterPill>
          <FilterPill active={filter === 'pipeline'} onClick={() => setFilter('pipeline')}>Pipeline</FilterPill>
          <FilterPill active={filter === 'admin'} onClick={() => setFilter('admin')}>Admin tool</FilterPill>
        </div>

        {loading && <div style={muted}>Loading…</div>}
        {error && <div style={errorBox}>{error}</div>}
        {!loading && entries && visible.length === 0 && (
          <div style={muted}>
            {entries.length === 0
              ? 'No changes recorded yet.'
              : 'No entries match this filter.'}
          </div>
        )}

        <ol style={timeline}>
          {visible.map((entry) => (
            <Entry key={entry.changeId} entry={entry} />
          ))}
        </ol>
      </div>
    </Modal>
  );
};

const Entry: React.FC<{ entry: CgBoostChangeLogEntry }> = ({ entry }) => {
  const isCreated = entry.field === '__created';
  return (
    <li style={item}>
      <div style={itemHeader}>
        <Pill tone={SOURCE_TONE[entry.source]} size="sm">{SOURCE_LABEL[entry.source]}</Pill>
        <span style={fieldLabel}>{isCreated ? 'Campaign created' : entry.field}</span>
        <div style={{ flex: 1 }} />
        <span style={timeLabel}>{formatTimeAgo(entry.at)}</span>
      </div>
      <div style={meta}>by {entry.byEmail || entry.byUid}</div>
      {!isCreated && (
        <div style={diffBlock}>
          <div style={diffSide}>
            <div style={diffSideLabel}>Before</div>
            <pre style={diffPre}>{stringify(entry.before)}</pre>
          </div>
          <div style={diffArrow}>→</div>
          <div style={diffSide}>
            <div style={diffSideLabel}>After</div>
            <pre style={diffPre}>{stringify(entry.after)}</pre>
          </div>
        </div>
      )}
      {isCreated && (
        <div style={createdBlock}>
          <pre style={diffPre}>{stringify(entry.after)}</pre>
        </div>
      )}
    </li>
  );
};

const FilterPill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active, onClick, children,
}) => (
  <Pill tone={active ? 'accent' : 'neutral'} active={active} size="sm" onClick={onClick}>
    {children}
  </Pill>
);

function stringify(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

const body: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 14 };
const subtitle: React.CSSProperties = {
  fontSize: 13, color: 'var(--fg-2)',
};
const filterRow: React.CSSProperties = { display: 'flex', gap: 6, flexWrap: 'wrap' };
const muted: React.CSSProperties = { fontSize: 13, color: 'var(--fg-3)', fontStyle: 'italic' };
const errorBox: React.CSSProperties = {
  background: 'var(--danger-soft)',
  color: 'var(--danger)',
  padding: 10,
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
};

const timeline: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};
const item: React.CSSProperties = {
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: 12,
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const itemHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
};
const fieldLabel: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 12.5,
  color: 'var(--fg-1)',
  background: 'var(--bg-hover)',
  padding: '2px 6px',
  borderRadius: 4,
};
const timeLabel: React.CSSProperties = { fontSize: 12, color: 'var(--fg-3)' };
const meta: React.CSSProperties = { fontSize: 12, color: 'var(--fg-3)' };
const diffBlock: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  gap: 8,
  alignItems: 'center',
};
const diffSide: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
};
const diffSideLabel: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, color: 'var(--fg-3)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
};
const diffArrow: React.CSSProperties = {
  fontSize: 14, color: 'var(--fg-3)', alignSelf: 'center',
};
const diffPre: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11.5,
  background: 'var(--bg-hover)',
  padding: 8,
  borderRadius: 'var(--radius-sm)',
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 160,
  overflowY: 'auto',
};
const createdBlock: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };

export default HistoryDrawer;
